import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { agentConversationsApi } from "@/api/agent-conversations";
import type { AgentConversation, AgentConversationMessage } from "@/api/agent-conversations";
import { agentsApi } from "@/api/agents";
import { queryKeys } from "@/lib/query-keys";
import { MessagesSquare, ArrowRight, Send, ChevronDown, ChevronRight } from "lucide-react";
import ReactMarkdown from "react-markdown";

function MessageBubble({ msg }: { msg: AgentConversationMessage }) {
  const isOutbound = msg.direction === "outbound";
  return (
    <div className={`flex ${isOutbound ? "justify-end" : "justify-start"} mb-3`}>
      <div
        className={`max-w-[80%] rounded-lg px-4 py-3 text-sm ${
          isOutbound
            ? "bg-accent/10 text-text-primary"
            : "bg-surface-hover text-text-primary"
        }`}
      >
        <div className="mb-1 flex items-center gap-2">
          <Badge variant={isOutbound ? "default" : "secondary"} className="text-[10px]">
            {isOutbound ? "Sent" : "Reply"}
          </Badge>
          <span className="text-[10px] text-text-muted">
            {new Date(msg.createdAt).toLocaleString()}
          </span>
        </div>
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown>{msg.content}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

function ConversationRow({
  convo,
  isExpanded,
  onToggle,
}: {
  convo: AgentConversation;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.agentConversations.messages(convo.id),
    queryFn: () => agentConversationsApi.getMessages(convo.id),
    enabled: isExpanded,
  });

  return (
    <Card>
      <CardContent className="py-3">
        <button
          onClick={onToggle}
          className="flex w-full items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
              <MessagesSquare size={18} className="text-accent" />
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                <span>{convo.fromAgentName}</span>
                <ArrowRight size={14} className="text-text-muted" />
                <span>{convo.toAgentName}</span>
              </div>
              <p className="text-xs text-text-muted">
                {convo.fromAgentRole} to {convo.toAgentRole}
                {convo.sessionKey && ` | Session: ${convo.sessionKey}`}
                {" | "}
                {new Date(convo.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown size={16} className="text-text-muted" />
          ) : (
            <ChevronRight size={16} className="text-text-muted" />
          )}
        </button>

        {isExpanded && (
          <div className="mt-4 border-t border-border pt-4">
            {isLoading ? (
              <p className="text-xs text-text-muted">Loading messages...</p>
            ) : data?.messages.length === 0 ? (
              <p className="text-xs text-text-muted">No messages yet.</p>
            ) : (
              <div className="space-y-1">
                {data?.messages.map((msg) => (
                  <MessageBubble key={msg.id} msg={msg} />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SendMessageDialog({
  companyId,
  onClose,
}: {
  companyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [fromAgentId, setFromAgentId] = useState("");
  const [targetAgentId, setTargetAgentId] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState<string | null>(null);

  const { data: agents = [] } = useQuery({
    queryKey: queryKeys.agents.byCompany(companyId),
    queryFn: () => agentsApi.listByCompany(companyId),
  });

  const sendMutation = useMutation({
    mutationFn: () => agentsApi.messageAgent(fromAgentId, targetAgentId, message),
    onSuccess: (data) => {
      setReply(data.reply);
      queryClient.invalidateQueries({
        queryKey: queryKeys.agentConversations.byCompany(companyId),
      });
    },
  });

  return (
    <Card className="mb-4">
      <CardContent className="space-y-3 pt-4">
        <h3 className="text-sm font-medium text-text-primary">Send Agent-to-Agent Message</h3>
        <div className="flex gap-3">
          <select
            value={fromAgentId}
            onChange={(e) => setFromAgentId(e.target.value)}
            className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
          >
            <option value="">From agent...</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.role})
              </option>
            ))}
          </select>
          <ArrowRight size={20} className="mt-2 shrink-0 text-text-muted" />
          <select
            value={targetAgentId}
            onChange={(e) => setTargetAgentId(e.target.value)}
            className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
          >
            <option value="">To agent...</option>
            {agents
              .filter((a) => a.id !== fromAgentId)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.role})
                </option>
              ))}
          </select>
        </div>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your message..."
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
          rows={3}
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={!fromAgentId || !targetAgentId || !message.trim() || sendMutation.isPending}
            onClick={() => sendMutation.mutate()}
          >
            <Send size={14} className="mr-1.5" />
            {sendMutation.isPending ? "Sending..." : "Send Message"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>

        {sendMutation.isError && (
          <p className="text-xs text-red-500">
            Error: {(sendMutation.error as Error).message}
          </p>
        )}

        {reply !== null && (
          <div className="rounded-lg border border-border bg-surface-hover p-3">
            <p className="mb-1 text-xs font-medium text-text-secondary">Reply:</p>
            <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
              <ReactMarkdown>{reply}</ReactMarkdown>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AgentComms() {
  const { tenantId } = useTenant();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showSend, setShowSend] = useState(false);

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: queryKeys.agentConversations.byCompany(tenantId),
    queryFn: () => agentConversationsApi.listByCompany(tenantId),
  });

  return (
    <CenterPanel title="Agent Communications">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {conversations.length} conversation{conversations.length !== 1 ? "s" : ""}
        </p>
        <Button size="sm" onClick={() => setShowSend(!showSend)}>
          <Send size={16} className="mr-1.5" />
          New Message
        </Button>
      </div>

      {showSend && (
        <SendMessageDialog companyId={tenantId} onClose={() => setShowSend(false)} />
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading conversations...</p>
      ) : conversations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <MessagesSquare size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No agent-to-agent conversations yet.</p>
            <p className="mt-1 text-xs text-text-muted">
              Agents will communicate through the platform proxy, and conversations will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {conversations.map((convo) => (
            <ConversationRow
              key={convo.id}
              convo={convo}
              isExpanded={expandedId === convo.id}
              onToggle={() =>
                setExpandedId(expandedId === convo.id ? null : convo.id)
              }
            />
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
